/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, ClickAwayListener, InputAdornment } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import { Query_Keys } from "../../lib/queryKeys";
import api from "../../api";
import { useDebounce } from "../../utils/useDebounce";
import { useNavigate } from "react-router-dom";
import {
  formatRestaurantsLabel,
  getFallbackAcronym,
  getGuestResultKey,
  getProfileBadgeColor,
} from './helpers';
import {
  StyledGlobalSearchContainer,
  StyledGlobalSearchField,
  StyledSearchDivider,
  StyledSearchDropdown,
  StyledSearchDropdownContent,
  StyledSearchProfileBadge,
  StyledSearchRestaurant,
  StyledSearchRestaurantLabel,
  StyledSearchResultDetails,
  StyledSearchResultHeader,
  StyledSearchResultIdentity,
  StyledSearchResultItem,
  StyledSearchResultMeta,
  StyledSearchResultName,
  StyledSearchResultPhone,
  StyledSearchStatus,
  StyledSearchTypeChip,
  StyledViewMoreButton,
} from "./styles";
import { CACHE_DURATION } from "../../constants/cache_duration";

const MAX_VISIBLE_RESULTS = 10;

export const GlobalSearchBar = ({ isMobile }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showAllResults, setShowAllResults] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const resultRefs = useRef([]);

  const trimmedSearch = search.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearch, 300);

  const searchGuests = async () => {
    const response = await api.get('/v1/admin/guest_search', {
      params: {
        search: debouncedSearchTerm,
      },
    });

    return response?.data || [];
  };

  const { data: guests = [], isFetching } = useQuery({
    queryKey: Query_Keys.search_guests({
      search_term: debouncedSearchTerm,
    }),
    queryFn: searchGuests,
    enabled: debouncedSearchTerm.length > 0,
    staleTime: CACHE_DURATION,
  });

  const isWaitingForDebounce = trimmedSearch.length > 0 && trimmedSearch !== debouncedSearchTerm;
  const shouldShowDropdown = isDropdownOpen && trimmedSearch.length > 0;

  const visibleGuests = useMemo(() => {
    if (showAllResults) {
      return guests;
    }

    return guests.slice(0, MAX_VISIBLE_RESULTS);
  }, [guests, showAllResults]);

  const shouldShowViewMore = guests.length > MAX_VISIBLE_RESULTS && !showAllResults;
  const showLoadingState = shouldShowDropdown && (isWaitingForDebounce || isFetching);
  const showEmptyState = shouldShowDropdown && !showLoadingState && guests.length === 0;

  useEffect(() => {
    setShowAllResults(false);
    setHighlightedIndex(-1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (!trimmedSearch.length) {
      setIsDropdownOpen(false);
      setHighlightedIndex(-1);
    }
  }, [trimmedSearch]);

  useEffect(() => {
    if (!shouldShowDropdown || visibleGuests.length === 0) {
      setHighlightedIndex(-1);
      return;
    }

    setHighlightedIndex((currentIndex) => {
      if (currentIndex < 0) {
        return -1;
      }

      return Math.min(currentIndex, visibleGuests.length - 1);
    });
  }, [shouldShowDropdown, visibleGuests]);

  useEffect(() => {
    if (highlightedIndex < 0) {
      return;
    }

    const highlightedResult = resultRefs.current[highlightedIndex];
    highlightedResult?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  const handleSearchClear = () => {
    setSearch('');
    setIsDropdownOpen(false);
    setShowAllResults(false);
    setHighlightedIndex(-1);
  };

  const handleSearchChange = (event) => {
    const nextValue = event.target.value;

    setSearch(nextValue);
    setIsDropdownOpen(nextValue.trim().length > 0);
    setHighlightedIndex(-1);
  };

  const handleNavigate = (guestData) => {
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
    navigate(`/admin/guest_details/${guestData.id}`);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleSearchClear();
      return;
    }

    if (!trimmedSearch.length || showLoadingState || visibleGuests.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsDropdownOpen(true);
      setHighlightedIndex((currentIndex) => {
        if (currentIndex < 0) {
          return 0;
        }

        return (currentIndex + 1) % visibleGuests.length;
      });
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIsDropdownOpen(true);
      setHighlightedIndex((currentIndex) => {
        if (currentIndex < 0) {
          return visibleGuests.length - 1;
        }

        return (currentIndex - 1 + visibleGuests.length) % visibleGuests.length;
      });
      return;
    }

    if (event.key === 'Enter' && highlightedIndex >= 0) {
      event.preventDefault();
      handleNavigate(visibleGuests[highlightedIndex]);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => {
      setIsDropdownOpen(false);
      setHighlightedIndex(-1);
    }}>
      <StyledGlobalSearchContainer isMobile={isMobile} data-testid="global-search-container">
        <StyledGlobalSearchField
          value={search}
          placeholder={isMobile ? 'Search employee/guest' : 'Search employee or guest'}
          onFocus={() => setIsDropdownOpen(trimmedSearch.length > 0)}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          inputProps={{
            'data-testid': 'global-search-input',
            role: 'combobox',
            'aria-autocomplete': 'list',
            'aria-expanded': shouldShowDropdown,
            'aria-controls': shouldShowDropdown ? 'global-search-listbox' : undefined,
            'aria-activedescendant': highlightedIndex >= 0 ? `global-search-option-${highlightedIndex}` : undefined,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#64748b', fontSize: '20px' }} />
              </InputAdornment>
            ),
            endAdornment: search.length > 0 && (
              <InputAdornment position="end">
                <ClearIcon
                  sx={{ cursor: 'pointer', color: '#94a3b8', fontSize: '20px' }}
                  onClick={handleSearchClear}
                  data-testid="global-search-clear"
                />
              </InputAdornment>
            ),
          }}
        />

        {shouldShowDropdown && (
          <StyledSearchDropdown elevation={0} data-testid="global-search-dropdown">
            <StyledSearchDropdownContent id="global-search-listbox" role="listbox" aria-label="Global search results">
              {showLoadingState && (
                <StyledSearchStatus data-testid="global-search-status">
                  Searching...
                </StyledSearchStatus>
              )}

              {showEmptyState && (
                <StyledSearchStatus data-testid="global-search-empty-state">
                  No result found
                </StyledSearchStatus>
              )}

              {!showLoadingState && visibleGuests.map((guest, index) => {
                const badgeColor = getProfileBadgeColor(guest?.profile_badge);
                const restaurantLabel = formatRestaurantsLabel(guest?.restaurants);

                return (
                  <React.Fragment key={getGuestResultKey(guest, index)}>
                    <StyledSearchResultItem
                      id={`global-search-option-${index}`}
                      ref={(node) => {
                        resultRefs.current[index] = node;
                      }}
                      data-testid="global-search-result-item"
                      role="option"
                      aria-selected={highlightedIndex === index}
                      tabIndex={-1}
                      highlighted={highlightedIndex === index}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => handleNavigate(guest)}
                    >
                      <StyledSearchResultIdentity>
                        <StyledSearchProfileBadge badgeColor={badgeColor}>
                          {guest?.acronym || getFallbackAcronym(guest?.name)}
                        </StyledSearchProfileBadge>

                        <StyledSearchResultDetails>
                          <StyledSearchResultHeader>
                            <StyledSearchResultName data-testid="global-search-result-name">
                              {guest?.name}
                            </StyledSearchResultName>

                            {guest?.type?.map((guestType) => (
                              <StyledSearchTypeChip
                                key={`${guest?.id}-${guestType}`}
                                label={guestType}
                                size="small"
                                data-testid="global-search-type-chip"
                              />
                            ))}
                          </StyledSearchResultHeader>

                          <StyledSearchResultMeta>
                            <LocalPhoneOutlinedIcon sx={{ fontSize: '12px', color: '#94a3b8' }} />
                            <StyledSearchResultPhone data-testid="global-search-result-phone">
                              {guest?.phone_number}
                            </StyledSearchResultPhone>
                          </StyledSearchResultMeta>
                        </StyledSearchResultDetails>
                      </StyledSearchResultIdentity>

                      <StyledSearchRestaurant>
                        <StyledSearchRestaurantLabel data-testid="global-search-result-restaurants">
                          {restaurantLabel}
                        </StyledSearchRestaurantLabel>
                        <KeyboardArrowRightRoundedIcon sx={{ fontSize: '18px', color: '#94a3b8' }} />
                      </StyledSearchRestaurant>
                    </StyledSearchResultItem>

                    {(index < visibleGuests.length - 1 || shouldShowViewMore) && <StyledSearchDivider />}
                  </React.Fragment>
                );
              })}

              {!showLoadingState && shouldShowViewMore && (
                <Box>
                  <StyledViewMoreButton
                    onClick={() => setShowAllResults(true)}
                    data-testid="global-search-view-more"
                  >
                    View more +
                  </StyledViewMoreButton>
                </Box>
              )}
            </StyledSearchDropdownContent>
          </StyledSearchDropdown>
        )}
      </StyledGlobalSearchContainer>
    </ClickAwayListener>
  );
};
